import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  Input,
  FormErrorMessage,
  SimpleGrid,
  Box,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  Icon,
  Heading,
  Textarea,
  Checkbox,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { Controller } from "react-hook-form";
import validator from "validator";
import SelectAdvanced from "@/components/common/SelectAdvanced";
import { Globe, Mail, Phone, User } from "react-feather";

import { useUser } from "@/hooks/useUser";

type Inputs = {
  name: string;
  url: string;
  officeEmail: string;
  phone: string;
  position: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  type: string;
  representative: string;
  message?: string;
  authorizedPerson?: boolean;
};

type Props = {
  panel?: boolean;
  withRequest?: boolean;
  isSuperadmin?: boolean;
  suggest?: boolean;
  activated?: boolean;
};

const EditorialOfficeForm = ({
  panel = false,
  withRequest = false,
  isSuperadmin = false,
  suggest = false,
  activated,
}: Props): React.ReactElement => {
  const {
    formState: { errors },
    register,
    control,
    watch,
    setValue,
  } = useFormContext<Inputs>();
  const { t } = useTranslation("common");
  const authorizedPerson = watch("authorizedPerson");
  const [session] = useUser();

  const options = useMemo(
    () => [
      {
        label: t("editorialOffice.press"),
        value: "press",
      },
      {
        label: t("editorialOffice.tv"),
        value: "tv",
      },
      {
        label: t("editorialOffice.radio"),
        value: "radio",
      },
      {
        label: t("editorialOffice.website"),
        value: "website",
      },
      {
        label: t("editorialOffice.agency"),
        value: "agency",
      },
      {
        label: t("editorialOffice.influencer"),
        value: "influencer",
      },
      {
        label: t("editorialOffice.other"),
        value: "other",
      },
    ],
    []
  );

  useEffect(() => {
    if (suggest)
      if (authorizedPerson)
        setValue(
          "representative",
          `${session?.user?.firstName} ${session?.user?.lastName}`
        );
      else setValue("representative", null);
  }, [authorizedPerson, suggest]);

  return (
    <>
      {!panel && (
        <Heading as="h3" size="lg">
          {t("editorialOffice.fillOfficeData")}
        </Heading>
      )}
      {suggest && (
        <FormControl mb={8}>
          <Checkbox {...register("authorizedPerson")}>
            {t("editorialOffice.imAuthorizedPerson")}
          </Checkbox>
        </FormControl>
      )}
      <SimpleGrid
        columns={{ base: 1, md: panel ? 2 : 1 }}
        spacingX={panel ? 3 : 5}
        spacingY={panel ? 3 : "15px"}
      >
        <FormControl isInvalid={!!errors.name} isRequired>
          <Box>
            {panel && (
              <FormLabel htmlFor="name">
                {t("editorialOffice.editorialName")}
              </FormLabel>
            )}
            <Input
              disabled={activated && !isSuperadmin ? true : false}
              autoFocus
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("editorialOffice.editorialName")}
              type="text"
              {...register("name", { required: `${t("users.fieldRequired")}` })}
            />
            {errors.name && (
              <FormErrorMessage>{errors.name.message}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl isInvalid={!!errors.representative} isRequired>
          <Box>
            {panel && (
              <FormLabel htmlFor="representative">
                {t("editorialOffice.representative")}
              </FormLabel>
            )}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={User} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("editorialOffice.representative")}
                type="text"
                disabled={authorizedPerson}
                {...register("representative", { required: true })}
              />
            </InputGroup>
            {errors.representative && (
              <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl
          isInvalid={!!errors.officeEmail}
          isRequired={!isSuperadmin}
        >
          <Box>
            {panel && (
              <FormLabel htmlFor="officeEmail">{t("users.email")}</FormLabel>
            )}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Mail} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.email")}
                type="email"
                {...register("officeEmail", {
                  required: !isSuperadmin
                    ? `${t("users.fieldRequired")}`
                    : false,
                  validate: (value) =>
                    !value || validator.isEmail(value)
                      ? true
                      : `${t("users.emailValidationError")}`,
                })}
              />
            </InputGroup>
            {errors.officeEmail && (
              <FormErrorMessage>{errors.officeEmail.message}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl isInvalid={!!errors.phone} isRequired={!isSuperadmin}>
          <Box>
            {panel && <FormLabel htmlFor="phone">{t("users.phone")}</FormLabel>}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Phone} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.phone")}
                type="text"
                {...register("phone", { required: !isSuperadmin })}
              />
            </InputGroup>
            {errors.phone && (
              <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
            )}
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="address">{t("address")}</FormLabel>}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("address")}
              type="text"
              {...register("address")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="city">{t("users.city")}</FormLabel>}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.city")}
              type="text"
              {...register("city")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && (
              <FormLabel htmlFor="postalCode">
                {t("users.postalCode")}
              </FormLabel>
            )}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.postalCode")}
              type="text"
              {...register("postalCode")}
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box>
            {panel && (
              <FormLabel htmlFor="country">{t("users.country")}</FormLabel>
            )}
            <Input
              size={panel ? "md" : "lg"}
              placeholder={panel ? "" : t("users.country")}
              type="text"
              {...register("country")}
            />
          </Box>
        </FormControl>
        <FormControl isInvalid={!!errors.type} isRequired>
          <Box>
            {panel && (
              <FormLabel htmlFor="type">
                {t("editorialOffice.editorialType")}
              </FormLabel>
            )}
            <Controller
              control={control}
              name="type"
              rules={{ required: true }}
              render={({ field: { onChange, value, ref } }) => (
                <>
                  <SelectAdvanced
                    inputRef={ref}
                    value={options.find((c) => value?.includes(c.value))}
                    onChange={(val) => onChange(val?.value)}
                    lg={panel ? false : true}
                    options={options}
                    placeholder={
                      panel ? t("choose") : t("editorialOffice.editorialType")
                    }
                    isInvalid={!!errors.type}
                  />
                </>
              )}
            />
          </Box>
          {errors.type && (
            <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl>
          <Box>
            {panel && <FormLabel htmlFor="url">{t("users.url")}</FormLabel>}
            <InputGroup>
              <InputLeftAddon>
                <Icon as={Globe} />
              </InputLeftAddon>
              <Input
                size={panel ? "md" : "lg"}
                placeholder={panel ? "" : t("users.url")}
                type="text"
                {...register("url")}
              />
            </InputGroup>
          </Box>
        </FormControl>
      </SimpleGrid>
      {withRequest && !authorizedPerson && (
        <Box mt={5}>
          <FormControl isInvalid={!!errors.message} isRequired>
            {panel && (
              <FormLabel htmlFor="message">
                {t(
                  "editorialOffice.accreditationInformation.suggestOfficeRequestMessage"
                )}
              </FormLabel>
            )}
            <Textarea
              type="text"
              {...register("message", { required: true })}
            />
            {errors.message && (
              <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
            )}
          </FormControl>
        </Box>
      )}
    </>
  );
};

export default EditorialOfficeForm;
